import type { IntrinsicSystemProps, MotionPropsFor } from '@hover/blueprint';
import {
  forwardRef,
  motion,
  useBreakpointValue,
  useImage,
} from '@hover/blueprint';
import { includes } from '@hover/utilities';
import React from 'react';

import { isGeneratingImage } from 'src/modules/homeownerApp/helpers';
import {
  useAltTextForImage,
  useHomeownerAppContext,
  useHomeownerTranslate,
} from 'src/modules/homeownerApp/hooks';
import { getImagePath, isPaintProduct } from 'src/modules/homeownerApp/types';
import { ZoomButton } from 'src/shared/components/Zoom';
import { buttonPressed } from 'src/shared/telemetry';
import { isServer } from 'src/shared/utils/environment';

import { usePartnerProductConfiguration } from '../partners';
import type { CustomizationHistoryEntry } from '../useCustomization';
import { useCustomization } from '../useCustomization';
import { useCustomizeTargetTracking } from '../useCustomizeTargetTracking';

import { BookmarkButton } from './BookmarkButton';
import { CustomizingOverlay } from './CustomizingOverlay';
import { EditedBadge } from './EditedBadge';
import type { HistoryImageProps } from './HistoryImage';
import { HistoryImage } from './HistoryImage';
import { imagePadding } from './constants';
import {
  applyActiveStyles,
  useEntryImage,
  useUndoRedoAriaLabel,
} from './utils';

interface HistoryEntryProps
  extends MotionPropsFor<IntrinsicSystemProps<'button'>> {
  index: number;
  isDragging?: boolean;
  isLayoutAnimated?: boolean;
  imageProps?: Partial<HistoryImageProps>;
  zoomOnClick?: (imageToZoom?: string) => void;
  exitingZoom?: boolean;
}

const HistoryEntry = forwardRef<HistoryEntryProps, 'div'>(
  (
    {
      imageProps,
      index,
      isDragging = false,
      isLayoutAnimated,
      zoomOnClick,
      exitingZoom = false,
      ...props
    },
    ref,
  ) => {
    const t = useHomeownerTranslate();
    const { category, isCustomizing, history, targetImage } =
      useCustomization();

    const { trackingData: contextTrackingData } = useHomeownerAppContext();
    const customizeTargetTracking = useCustomizeTargetTracking();

    const { entries, activeIndex } = history;

    const isMobile = useBreakpointValue({ base: true, tablet: false });

    const previousEntry = entries[index - 1] as
      | CustomizationHistoryEntry
      | undefined;
    const currentEntry = entries[index] as
      | CustomizationHistoryEntry
      | undefined;

    const { aiGeneratedImageId: currentImageId } = currentEntry ?? {};

    const { data: previousData } = useEntryImage(index - 1);
    const { data: currentData } = useEntryImage(index);

    const previousImage = previousData?.aiGeneratedImage;
    const currentImage = currentData?.aiGeneratedImage;

    const alt = useAltTextForImage(currentImage);
    const label = useUndoRedoAriaLabel(index);

    const currentImageState = useImage({
      src: getImagePath(currentImage),
    });

    const configuration = usePartnerProductConfiguration();
    const labelI18nKey = configuration?.category.i18nKeyPrimarySecondaryLabel;

    if (!previousEntry || !currentEntry || !currentEntry.customization) {
      return null;
    }

    const isCustomizingImage = !currentImage || isGeneratingImage(currentImage);
    const isLoadingImage =
      isServer() || includes(['pending', 'loading'], currentImageState);

    const isCustomizingOrLoading = exitingZoom
      ? false
      : isCustomizingImage || isLoadingImage;

    const {
      customization: { product },
    } = currentEntry;

    const isActive = index === activeIndex;

    const handleClick = () => {
      buttonPressed({
        ...contextTrackingData,
        ...customizeTargetTracking,
        button_text: label ?? '',
        edited_image_id: currentImageId ?? undefined,
        track_id: 'edit-history-navigation',
      });
      history.select(index);
    };

    return (
      <motion.button
        aria-label={label}
        background="none"
        border="none"
        display="flex"
        height="100%"
        initial={{
          opacity: 0,
          transform: `translateX(${index * 100}%)`,
          filter: 'brightness(1) saturate(1)',
        }}
        onClick={isActive || isDragging ? undefined : handleClick}
        padding={0}
        position="absolute"
        width="100%"
        {...applyActiveStyles({
          isActive,
          isVisible: !!category || isActive,
          animate: {
            transform: `translateX(${index * 100}%)`,
          },
        })}
        {...props}
        ref={ref}
      >
        <HistoryImage
          aria-hidden
          filter="brightness(0.6)"
          height="100%"
          position="absolute"
          src={previousImage?.generatedImagePath}
          transform={isLayoutAnimated ? undefined : 'none !important'}
          width="100%"
          zIndex={1}
        />
        <HistoryImage
          alt={alt}
          animate={{ opacity: isCustomizingOrLoading ? 0 : 1 }}
          height="100%"
          position="absolute"
          src={currentImage?.generatedImagePath}
          transform={isLayoutAnimated ? undefined : 'none !important'}
          width="100%"
          zIndex={2}
          {...imageProps}
        />
        <CustomizingOverlay
          animate={{ opacity: isCustomizingOrLoading ? 1 : 0 }}
          bottom={imagePadding}
          customization={
            labelI18nKey
              ? t(labelI18nKey, {
                  primaryLabel: product.primaryLabel,
                  secondaryLabel: product.secondaryLabel,
                })
              : product.primaryLabel
          }
          initial={false}
          left={imagePadding}
          position="absolute"
          right={imagePadding}
          verb={
            isPaintProduct(product)
              ? t('paint.painting')
              : t('customize.adding')
          }
          zIndex={3}
        />
        {!currentImage?.fromSample && (
          <BookmarkButton
            image={history.activeIndex === 0 ? targetImage : currentImage}
            isVisible={!isLoadingImage}
            zIndex={3}
          />
        )}
        {zoomOnClick && isMobile ? (
          <ZoomButton
            isVisible={!isLoadingImage && isActive}
            onClick={() =>
              zoomOnClick(
                history.activeIndex === 0
                  ? targetImage.aiGeneratedImage.generatedImagePath
                  : currentImage?.generatedImagePath,
              )
            }
          />
        ) : (
          <EditedBadge
            image={currentImage}
            isLayoutAnimated={isLayoutAnimated}
            isVisible={
              !isActive || (isActive && !isLoadingImage && !isCustomizing)
            }
            zIndex={3}
          />
        )}
      </motion.button>
    );
  },
);
export { HistoryEntry };
